import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useAtomValue } from "jotai";
import { findIndex, findWhere } from "underscore";
import timezone from "dayjs/plugin/timezone";
import { atomFamily } from "jotai/utils";
import { Col, Row } from "reactstrap";
import dayjs from "dayjs";

import useQueriedRecords from "../../../../api/api-hooks/records/useQueriedRecords";
import useWorkspacePermissions from "../../../../hooks/useWorkspacePermissions";
import UpdateRecordsDrawer from "../../../Drawers/Records/UpdateRecordsDrawer";
import BulkApplyTagsDrawer from "../../../Drawers/Records/BulkApplyTagsDrawer";
import CreateRecordDrawer from "../../../Drawers/Records/CreateRecordDrawer";
import useUpdateView from "../../../../api/api-hooks/views/useUpdateView";
import DownloadTemplateModal from "../../../Modals/DownloadTemplateModal";
import useCloudPermissions from "../../../../hooks/useCloudPermissions";
import usePagePermissions from "../../../../hooks/usePagePermissions";
import { viewSelectLayoutHistory } from "../../../../atoms/ViewAtoms";
import { Layout, Prefill } from "../../../../types/GlobalInterfaces";
import DataExportDrawer from "../../../Drawers/DataExportDrawer";
import ImportCSVDrawer from "../../../Drawers/ImportCSVDrawer";
import useToggle from "../../../../hooks/useToggle";
import useModule from "../../../../hooks/useModule";
import DataSource from "../../../Menus/DataSource";
import TableSettings from "../TableSettings";

import BulkTransformFeedbackDrawer, {
  TransformFeedbackToIssue,
} from "../../../../views/supportal/FeedbackView/BulkTransformFeedbackDrawer";
import FeedbackTransformDrawer from "../../../../views/supportal/FeedbackView/FeedbackTransformDrawer";
import MergeRecordsDrawer from "../../../Drawers/Records/MergeRecordsDrawer/MergeRecordsDrawer";
import useBulkDeleteRecords from "../../../../api/api-hooks/records/useBulkDeleteRecords";
import {
  GroupedLayout,
  ReusableViewIDs,
  View,
} from "../../../../types/PageInterfaces";
import SharedTableLayoutsButton, {
  UpdateTLayout,
} from "./SharedTableLayoutsButton";
import BrieVoiceCommands from "../../../../views/brie/BrieAssist/BrieVoiceCommands";
import FormBuilderSuccessModal from "../../../FormBuilder/FormBuilderSuccessModal";
import useCreateIssues from "../../../../api/api-hooks/supportal/useCreateIssue";
import SharedFiltersButtonWithOptions from "./SharedFiltersButtonWithOptions";
import QueryUtils, { GetTableGroupBy } from "../../../../utils/QueryUtils";
import BulkDataTransforms from "../../../Drawers/BulkDataTransformsDrawer";
import DuplicateRecordDrawer from "../../../Drawers/DuplicateRecordDrawer";
import SharedSelectedRecordsActions from "./SharedSelectedRecordsActions";
import { layoutTypeMap } from "../../../TableLayouts/TableLayoutsButton";
import FormBuilderDrawer from "../../../FormBuilder/FormBuilderDrawer";
import useGlobals from "../../../../api/api-hooks/globals/useGlobal";
import { TimelineLayout } from "../PageViewBoard/PageViewTimeline";
import PageViewInsights from "../PageViewBoard/PageViewInsights";
import AddToListDrawer from "../../../Drawers/AddToListDrawer";
import useView from "../../../../api/api-hooks/views/useView";
import SharedViewRecordDrawer from "./SharedViewRecordDrawer";
import usePage from "../../../../api/api-hooks/pages/usePage";
import { SelectedChart } from "../../../Widgets/WidgetTypes";
import PageViewSubitemDrawer from "../PageViewSubitemDrawer";
import useUser from "../../../../api/api-hooks/auth/useUser";
import SharedQuickCreateMenu from "./SharedQuickCreateMenu";
import { Module } from "../../../../types/TenantInterfaces";
import InstanceUtils from "../../../../utils/InstanceUtils";
import { SharedViewModes } from "../../PageViewRenderer";
import useFieldMap from "../../../../hooks/useFieldMap";
import useDefaultPrefill from "./useDefaultPrefill";
import usePageViewTable from "../usePageViewTable";
import StrUtils from "../../../../utils/StrUtils";
import { IsMobileAtom } from "../../../../App";
import SharedSearch from "./SharedSearch";
import SharedSortBy from "./SharedSortBy";

dayjs.extend(timezone);

export type LayoutKey =
  | "TableLayouts"
  | "CardsLayouts"
  | "BoardLayouts"
  | "TimelineLayouts";

const emptyPrefill: Prefill[] = [];

export interface DetailViewInterface {
  moduleAPIName: string;
  recordID: string;
  tab: string;
  viewID: string;
}

export type DetailView = DetailViewInterface | null;

export const queryAtomFamily = atomFamily((_viewID?: string) =>
  atom({} as any)
);
export const createRecordFamily = atomFamily((_viewID?: string) => atom(false));
export const prefillFamily = atomFamily((_viewID?: string) =>
  atom<Prefill | null>(null)
);

const SharedToolbar = ({
  ChildComponent,
  view,
  parentViewID,
  viewMode,
  setViewMode,
  showInsights,
  record,
  reusableViews,
  commentsWidget,
}: {
  ChildComponent: any;
  view: View;
  parentViewID?: string;
  viewMode: SharedViewModes;
  setViewMode: (newMode: SharedViewModes) => void;
  showInsights?: boolean;
  record?: any;
  commentsWidget?: boolean;
  reusableViews?: ReusableViewIDs;
}) => {
  const isMobile = useAtomValue(IsMobileAtom);
  const [query, setQuery] = useAtom(queryAtomFamily(view?.id));
  const { module: currentModule } = useModule(view?.ModuleAPIName);
  const [selectedPrefill, setSelectedPrefill] = useAtom(
    prefillFamily(view?.id)
  );
  const defaultPrefill = useDefaultPrefill({ view });
  const [sLayout, setSelectedLayout] = useState<string | null>(null);
  const [openRecordData, setOpenRecordData] = useState<DetailView>(null);
  const [selected, setSelected] = useState({});
  const { pageID, workspaceID }: any = useParams();
  const { updateView, viewUpdating } = useUpdateView(pageID);
  const [layoutHistory, setLayoutHistory] = useAtom(viewSelectLayoutHistory);
  const { confirmBulkDelete, bulkDeleteRecords } = useBulkDeleteRecords();
  const { page } = usePage(pageID);
  const { user } = useUser();
  const client = useQueryClient();
  const recordFieldTypes = useFieldMap(currentModule?.APIName, "FieldType");
  const isCalendar = viewMode === "Calendar";

  const [importCSVDrawer, toggleImportCSVDrawer] = useToggle();
  const [createRecordsDrawer, setCreateRecordsDrawer] = useAtom(
    createRecordFamily(view?.id)
  );
  const [exportDrawer, setExportDrawer] = useState(false);
  const [addToListDrawer, setAddToListDrawer] = useState(false);
  const [duplicateRecordID, setDuplicateRecordID] = useState("");
  const [bulkFeedbackTransformDrawer, setBulkFeedbackTransformDrawer] =
    useState(false);
  const [feedbackDrawer, setFeedbackDrawer] = useState(false);
  const [feedbackRecord, setFeedbackRecord] = useState<any>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const [updateRecordsModal, toggleUpdateRecordsModal] = useToggle();
  const [bulkApplyTags, toggleBulkApplyTags] = useToggle();
  const [mergeDrawer, toggleMergeDrawer, setMergeDrawer] = useToggle();
  const [templateCSVModal, toggleTemplateCSVModal] = useToggle();
  const [tableSettings, toggleTableSettings] = useToggle();
  const [dTActive, toggleDataTransforms] = useToggle();
  const [createFormDrawer, setCreateFormDrawer] = useState(false);
  const [successFormID, setSuccessFormID] = useState("");
  const [draggingID, setDraggingID] = useState("");

  const [simpleSearchValue, setSimpleSearchValue] = useState("");
  const [simpleSearchQuery, setSimpleSearchQuery] = useState<any>(null);
  const [selectedGroup, setSelectedGroup] = useState<
    SelectedChart | undefined
  >();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(pageID);
  const [search, setSearch] = useSearchParams();
  const { updateItem } = usePageViewTable({
    currentViewID: view?.id,
    updateView,
    parentViewID,
  });
  const { createIssues } = useCreateIssues();

  const { view: inheritedView } = useView({
    pageID,
    viewID: reusableViews?.RecordDetail,
    disabled: !reusableViews || !reusableViews?.RecordDetail,
  });

  const { globals: gLayouts } = useGlobals<Layout[]>("Layouts");

  const firstTab = useMemo(() => {
    const tab = inheritedView
      ? inheritedView.SubViews?.[0]
      : view?.SubViews?.[0];

    let startsWith = inheritedView
      ? `Reuse-${reusableViews?.RecordDetail}-`
      : "";
    let activeValue = tab?.id;

    if (!tab || !activeValue) return startsWith + "Summary";

    if (tab.Type === "RichText") {
      activeValue = `RichText-${tab?.FieldAPIName}-${activeValue}`;
    }
    if (tab.Type === "Gallery") {
      activeValue = `Gallery-${tab?.FieldAPIName}-${activeValue}`;
    }
    if (tab.Type === "Subitem") {
      activeValue = `Subitem-${tab?.FieldAPIName}-${activeValue}`;
    }
    if (!!tab.ReusableView) {
      activeValue = `ReuseView-${activeValue}`;
    }

    return startsWith + activeValue;
  }, [view?.SubViews, inheritedView, reusableViews?.RecordDetail]);

  const variableValues = useMemo(() => {
    const vals: Record<string, any> = {
      user: user,
      functions: InstanceUtils.getDefaultValueFunctionOptions(),
      recordFieldTypes: recordFieldTypes,
    };

    if (record) vals.record = record;

    return vals;
  }, [record]);

  const layoutsToSearch = useMemo(() => {
    const layouts =
      view?.[
        layoutTypeMap?.[viewMode as SharedViewModes]?.itemKey as LayoutKey
      ];

    if (!layouts || !Array.isArray(layouts)) return [];

    return layouts?.map((l) => {
      if (l.GlobalID) {
        const foundGlobalLayout = findWhere(gLayouts, { id: l.GlobalID });

        if (foundGlobalLayout) {
          return { ...foundGlobalLayout, ...l };
        }
      }

      return l;
    }) as GroupedLayout[] | Layout[];
  }, [
    view?.[layoutTypeMap?.[viewMode as SharedViewModes]?.itemKey as LayoutKey],
  ]);

  const generateFilter = (
    groupByField: string | undefined,
    groupByValue: any
  ) => {
    if (!groupByField) return null;

    let groupByFieldAPIName = groupByField;

    if (groupByFieldAPIName.includes(".")) {
      groupByFieldAPIName = groupByFieldAPIName.split(".")[0];
    }

    if (groupByFieldAPIName === "_id") {
      return {
        Field: groupByField,
        Method: "equals",
        Value: groupByValue,
      };
    }

    const field = currentModule?.Fields?.find(
      (f: any) => f.APIName === groupByFieldAPIName
    );

    if (!field) return null;

    let method;

    if (field?.FieldType === "MultiReference") {
      method = "contains";
    } else {
      method = "equals";
    }

    if (
      groupByValue === null ||
      groupByValue === undefined ||
      groupByValue === ""
    ) {
      method = "is-empty";
    }

    if (
      (field?.FieldType === "Currency" ||
        field?.FieldType === "CurrencyInterval") &&
      !!groupByValue
    ) {
      groupByValue = parseFloat(groupByValue.replace("$", ""));
    }

    return {
      Field: groupByField,
      Method: method,
      Value: groupByValue,
    };
  };

  const selectedGroupFilter = useMemo(() => {
    if (!selectedGroup || !selectedGroup?.group?.length) return undefined;

    const groups = selectedGroup.group;
    const filters = groups.flatMap((group) => {
      if (group?.Group) {
        const groupedFilters: any[] = [];

        for (const subGroup of group.Groups) {
          const filter = generateFilter(
            subGroup?.groupByField,
            subGroup?.groupByValue
          );

          if (!filter) continue;

          groupedFilters.push(filter);
        }

        if (!groupedFilters?.length) return [];

        return {
          Group: true,
          Filters: groupedFilters,
        };
      }

      const filter = generateFilter(group?.groupByField, group?.groupByValue);

      if (!filter) return [];

      return {
        Group: true,
        Filters: [filter],
      };
    });

    if (selectedGroup?.filters) {
      const updatedFilters = QueryUtils.mergeFilters(
        filters,
        selectedGroup.filters
      );

      return updatedFilters;
    }

    return filters;
  }, [selectedGroup]);

  const selectedGroupQuery = useMemo(() => {
    if (simpleSearchQuery) return null;
    if (!selectedGroupFilter) return null;

    return QueryUtils.mergeTopLevelFilter(
      selectedGroupFilter,
      structuredClone(query)
    );
  }, [query, simpleSearchQuery, selectedGroupFilter]);

  const selectedLayout = getSelectedLayout();
  const foundLayout = manageFoundLayout();

  const { records, pagination, recordsLoading, error, errorMsg, queryKey } =
    useQueriedRecords({
      variableValues: variableValues,
      moduleAPIName: view?.ModuleAPIName ?? "",
      query: simpleSearchQuery || query,
      filterAggregations:
        viewMode === "Table" && !!query?.GetAllGrouped ? false : true,
      viewFilters: view?.ViewFilters?.Filters,
      useDefaultVariables: true,
      groupByQueryField:
        viewMode === "Board" || viewMode === "Timeline"
          ? (foundLayout as GroupedLayout)?.GroupBy
          : undefined,
      groupByInterval:
        viewMode === "Timeline"
          ? (foundLayout as TimelineLayout)?.TimeFrame
          : undefined,
      topLevelFilters: selectedGroupFilter,
      timeZone: viewMode === "Timeline" ? dayjs.tz.guess() : undefined,
    });
  const isSelection = useMemo(() => {
    const selectedRecordKeys = Object.keys(selected);
    const selectedRecordsLength = selectedRecordKeys.length;
    return selectedRecordsLength > 0;
  }, [selected]);

  const selectedRecords = useMemo(() => {
    const selectedRecordKeys = Object.keys(selected);
    if (!isSelection) return [];
    const out = [] as any[];
    let selectedRecordsIds: string[] = [];

    if (viewMode === "Board" || viewMode === "Timeline") {
      for (const group of records) {
        for (const record of group?.GroupBy?.records ?? []) {
          if (selectedRecordKeys.includes(record._id)) {
            out.push(record);
            selectedRecordsIds.push(record._id);
          }
        }
      }
    } else {
      for (const key of selectedRecordKeys) {
        if (records?.length && records[key]) {
          out.push(records[key]);
          selectedRecordsIds.push(records[key].id);
        }
      }
    }
    return out;
  }, [records, selected, isSelection]);

  const selectedRecordsIds = useMemo(() => {
    if (!isSelection) return [];
    return selectedRecords.map((r) => r.id);
  }, [selectedRecords, isSelection]);

  const viewRef = useRef<any>();
  const queryRef = useRef<any>();
  const simpleSearchQueryRef = useRef<any>();
  const queryKeyRef = useRef<any[]>();

  viewRef.current = view;
  queryRef.current = query;
  simpleSearchQueryRef.current = simpleSearchQuery;
  viewRef.current = view;
  queryKeyRef.current = queryKey;

  const clearSimpleSearch = useCallback(() => setSimpleSearchValue(""), []);

  /**
   * Change the query's sort by parameters
   */
  const updateSortBy = useCallback((sortBy: any) => {
    let q = queryRef.current;
    let otherQ = simpleSearchQueryRef.current;

    q.OrderBy = sortBy ? sortBy : null;
    otherQ && (otherQ.OrderBy = q.OrderBy);

    if (q.OrderBy === null) {
      delete q.OrderBy;
    }

    if (otherQ && otherQ.OrderBy === null) {
      delete otherQ.OrderBy;
    }

    q = { ...q };
    otherQ && (otherQ = { ...otherQ });

    otherQ && setSimpleSearchQuery(otherQ);
    setQuery(q);
  }, []);

  const updateLayout = useCallback(({ Fields, ...GroupBy }: UpdateTLayout) => {
    if (simpleSearchQueryRef.current) {
      return setSimpleSearchQuery((prev: any) => ({ ...prev, Fields }));
    }

    return setQuery((prev: any) => ({ ...prev, ...GroupBy, Fields }));
  }, []);

  const updateQuery = useCallback(
    (newQuery: any) => {
      updateView({
        viewID: view?.id as string,
        payload: { Query: newQuery },
        parentViewID,
      });
    },
    [view?.id, updateView]
  );

  const updateTableSettings = useCallback(
    (newSettings: any, onSuccess: () => void) => {
      updateView({
        viewID: view?.id as string,
        payload: newSettings,
        onSuccess: onSuccess,
        parentViewID,
      });
    },
    [view?.id, updateView]
  );

  const updateAggregation = useCallback(
    (field: any, method: string) => {
      const newQuery = { ...query };
      const newAggregations = [...query.Aggregations];

      const index = findIndex(newAggregations, { Field: field });

      if (index > -1) {
        if (method === "None") {
          newAggregations.splice(index, 1);
        } else {
          newAggregations[index].Method = method;
        }
      } else {
        newAggregations.push({ Field: field, Method: method });
      }

      newQuery.Aggregations = newAggregations;
      setQuery(newQuery);
    },
    [query]
  );

  /**
   * Adds or Removes a field to the query and current layout.
   * @param {object | string} field - The field OR field.APIName of the field.
   * @param {"Add" | "Remove"} action - The action you want to perform (Add or Remove field)
   */
  const updateQueryFields = useCallback(
    (field: any, action: "Add" | "Remove", reference: any) => {
      const view = viewRef.current;
      const query = queryRef.current;

      if (field && typeof field === "object") {
        if (reference) {
          field = `${reference}.${field.APIName}`;
        } else {
          field = field.SubitemParent
            ? `${field.SubitemParent}.${field.APIName}`
            : field?.APIName;
        }
      }

      if (!field) return;

      let newFields;
      let newLayouts;

      if (foundLayout) {
        newFields = [...foundLayout.Fields];

        if (action === "Add") {
          if (newFields?.indexOf(field) >= 0) return;

          newFields.push(field);
        } else if (action === "Remove") {
          newFields = newFields.filter((f) => f !== field);
        }

        const payload = {
          ...foundLayout,
          Fields: newFields,
        };

        // @ts-ignore
        newLayouts = updateItem({
          id: selectedLayout,
          key: "TableLayouts",
          payload: payload,
          dontCallAPI: true,
        });
      }

      const newQuery = { ...query };
      let fields = newFields ?? newQuery?.Fields ?? ["id", "_RecordName"];

      if (action === "Add" && !fields.includes(field)) {
        fields.push(field);
      } else if (action === "Remove") {
        fields = fields.filter((f: any) => f !== field);
      }

      newQuery.Fields = [...fields];

      setQuery(newQuery);

      // Update the Default Query (activeView.Query), if the SelectedLayout is the default,
      // or there is no selected layout and saved selected layout.
      // Else: Only update the current selected layout.
      if (
        (!selectedLayout && !view?.Query?._SelectedLayout) ||
        selectedLayout === view?.Query?._SelectedLayout
      ) {
        updateView({
          viewID: view?.id,
          payload: { Query: newQuery, TableLayouts: newLayouts },
          parentViewID,
        });
      } else {
        newLayouts &&
          updateView({
            viewID: view?.id,
            payload: { TableLayouts: newLayouts },
            parentViewID,
          });
      }
    },
    [updateView, selectedLayout, foundLayout]
  );

  const onColumnReorder = useCallback(
    async (newQuery: any) => {
      let newLayouts;
      const finalPayload: Record<string, any> = {};

      // Copy query to new query key to prevent refetch of same data.
      const qK = queryKeyRef.current!;
      const data = client.getQueryData(qK);

      const newQk = structuredClone(qK);
      newQk[3].Fields = structuredClone(newQuery?.Fields);

      if (newQk[3].Fields.indexOf("Tags") < 0) {
        newQk[3].Fields.push("Tags");
      }

      client.setQueryData(newQk, data);
      client.removeQueries({ queryKey: qK });

      // Update the current layout with the new field order.
      if (foundLayout) {
        const payload = { ...foundLayout, Fields: newQuery?.Fields };

        newLayouts = updateItem({
          id: selectedLayout,
          key: "TableLayouts",
          payload: payload,
          dontCallAPI: true,
          onSuccess: undefined,
        });

        finalPayload.TableLayouts = newLayouts;
      }

      setQuery(newQuery);

      if (
        (!selectedLayout && !view?.Query?._SelectedLayout) ||
        selectedLayout === view?.Query?._SelectedLayout
      ) {
        finalPayload.Query = newQuery;
      }

      if (Object.keys(finalPayload)?.length > 1) {
        updateView({
          viewID: view?.id,
          payload: { ...finalPayload },
          parentViewID,
        });
      } else if (finalPayload.TableLayouts) {
        updateView({
          viewID: view?.id,
          payload: { ...finalPayload },
          parentViewID,
        });
      }
    },
    [
      updateView,
      foundLayout,
      selectedLayout,
      parentViewID,
      view?.id,
      view?.Query,
    ]
  );

  const onCreateRecordDrawerClose = useCallback(() => {
    setCreateRecordsDrawer(false);
    setSelectedPrefill(null);
  }, []);

  const onCreateRecordDrawerCreate = useCallback(() => {
    setCreateRecordsDrawer(false);
    setSelectedPrefill(null);
    setSelected({});
  }, []);

  const onCreateForm = () => {
    setCreateRecordsDrawer(true);
    setCreateFormDrawer(true);
  };

  const handleCloseFormBuilder = () => {
    setCreateFormDrawer(false);
  };

  /**
   * Load the table information.
   */
  const loadTableInformation = useCallback(() => {
    let query = viewRef.current?.Query
      ? structuredClone(viewRef.current?.Query)
      : {};

    let paramsUpdated = false;

    // Allows a query to be passed through the URL
    if (search.get("q")) {
      paramsUpdated = true;
      // Get the "q" query param from the URL
      const q = search.get("q");

      if (q) {
        try {
          const decodedQuery = decodeURIComponent(q);
          const parsedQuery = JSON.parse(decodedQuery);

          query = { ...query, ...parsedQuery };
        } catch {}
      }

      search.delete("q");
    }

    // Allows a sort to be passed through the URL
    if (search.get("sort")) {
      paramsUpdated = true;

      const sort = search.get("sort");

      if (sort && sort !== "null") {
        try {
          // Should be an object with a Field and Direction property
          const parsedSort = JSON.parse(sort);

          // Validate the sort object since it comes from the URL
          if (
            Object.keys(parsedSort).length === 2 &&
            parsedSort.Field &&
            parsedSort.Direction
          ) {
            query = { ...query, ...parsedSort };
          }
        } catch {}
      }

      search.delete("sort");
    }

    if (search.get("prefill")) {
      paramsUpdated = true;

      const prefillName = search.get("prefill");

      if (prefillName && prefillName !== "null") {
        const foundPrefill = view?.Prefills?.find(
          (p) => p.Name === prefillName
        );

        if (foundPrefill) {
          setSelectedPrefill(foundPrefill);
          setCreateRecordsDrawer(true);
        }
      } else {
        setCreateRecordsDrawer(true);
      }

      search.delete("prefill");
    }

    paramsUpdated && setSearch(search, { replace: true });

    const isFoundLayout = foundLayout?.Fields?.length;
    const groupBy = GetTableGroupBy(isFoundLayout ? foundLayout : query);

    query.Fields = isFoundLayout ? foundLayout?.Fields : query.Fields;
    query.GetAllGrouped = groupBy.GetAllGrouped;
    query.GroupBy = groupBy.GroupBy;

    if (isFoundLayout && "CardImage" in foundLayout && foundLayout?.CardImage) {
      query = structuredClone(query);
      query.Fields.push(foundLayout.CardImage);
    }
    if (
      isFoundLayout &&
      "BackFields" in foundLayout &&
      foundLayout?.BackFields?.length
    ) {
      query = structuredClone(query);
      query.Fields = [...query.Fields, ...(foundLayout?.BackFields || [])];
    }

    setSimpleSearchQuery(null);
    setQuery(query);
  }, [view, foundLayout]);

  useEffect(() => {
    loadTableInformation();

    () => queryAtomFamily.remove(view?.id);
  }, [loadTableInformation]);

  const stableOpenRecordName = useCallback(
    (data: any) => {
      data.tab = firstTab;
      setOpenRecordData(data);
    },
    [firstTab]
  );

  const QuickCreateMenu = (
    <SharedQuickCreateMenu
      toggleCreateRecordsDrawer={() => setCreateRecordsDrawer(true)}
      toggleImportCSVDrawer={toggleImportCSVDrawer}
      setSelectedPrefill={setSelectedPrefill}
      view={view}
      brie={true}
      // @ts-ignore
      module={currentModule}
      parentViewID={parentViewID}
      record={record}
      defaultPrefill={defaultPrefill}
    />
  );

  const BrieButton = <BrieVoiceCommands />;

  const FiltersButton = (
    <SharedFiltersButtonWithOptions
      currentModule={currentModule!}
      query={query}
      setQuery={setQuery}
      simpleSearchValue={simpleSearchValue}
      setSimpleSearchValue={setSimpleSearchValue}
      clearSimpleSearch={clearSimpleSearch}
      view={view}
    />
  );

  const SortBy = isCalendar ? null : (
    <SharedSortBy
      query={query}
      updateSortBy={updateSortBy}
      view={view}
      parentViewID={parentViewID}
    />
  );

  const DataSourceButton = (
    <DataSource
      module={currentModule}
      isVisible={true}
      setExportDrawer={setExportDrawer}
      toggleTableSettings={toggleTableSettings}
      tableSettingsDisabled={
        !cPermissions.cloud_modify_page &&
        !wPermissions.workspace_modify_page &&
        !pPermissions.page_modify_view
      }
      availableOptions={[
        "ViewData",
        "ExportData",
        "ChangeDataSource",
        "ManageDataSource",
        ...(viewMode === "Table" ? ["TableSettings"] : []),
      ]}
      viewMode={viewMode}
    />
  );

  const SharedTableLayoutsBtn = (
    <SharedTableLayoutsButton
      query={query}
      foundLayout={foundLayout}
      selectedLayout={selectedLayout}
      setQuery={setQuery}
      currentModule={currentModule!}
      updateQuery={updateQuery}
      setSelectedLayout={setSelectedLayout}
      updateLayout={updateLayout}
      view={view}
      parentViewID={parentViewID}
      viewMode={viewMode}
      setSelected={setSelected}
      setViewMode={setViewMode}
    />
  );

  const ChildComponentRendered = (
    <ChildComponent
      currentModule={currentModule}
      simpleSearchQuery={simpleSearchQuery}
      query={selectedGroupQuery || query}
      setQuery={(query: any) => {
        if (simpleSearchQuery) setSimpleSearchQuery(query);
        else setQuery(query);
      }}
      foundLayout={foundLayout}
      updateSortBy={updateSortBy}
      updateQuery={updateQuery}
      selected={selected}
      setSelected={setSelected}
      activeView={view}
      records={records}
      pagination={pagination}
      recordsLoading={recordsLoading}
      error={error}
      errorMsg={errorMsg}
      moduleAPIName={currentModule?.APIName}
      selectableRows={cPermissions?.cloud_create_record}
      onSort={updateSortBy}
      onUpdateQuery={updateQuery}
      columnAdd={true}
      columnAddMessage={`Adding Field to ${
        foundLayout?.Name ?? "Default Layout"
      }`}
      onColumnAdd={(field: any, reference: any) =>
        updateQueryFields(field, "Add", reference)
      }
      onColumnRemove={(field: any, reference: any) =>
        updateQueryFields(field, "Remove", reference)
      }
      onColumnReorder={onColumnReorder}
      selectedRecords={selected}
      setSelectedRecords={setSelected}
      data={records}
      loading={recordsLoading}
      toggleCreateRecordsDrawer={() => setCreateRecordsDrawer(true)}
      onOpenRecordName={stableOpenRecordName}
      isSelection={isSelection}
      setExportDrawer={setExportDrawer}
      loadTableInformation={loadTableInformation}
      toggleUpdateRecordsModal={toggleUpdateRecordsModal}
      toggleBulkApplyTags={toggleBulkApplyTags}
      toggleTemplateCSVModal={toggleTemplateCSVModal}
      deleteSelectedRecords={deleteSelectedRecords}
      toggleMergeDrawer={toggleMergeDrawer}
      handleDuplicateRecord={handleDuplicateRecord}
      selectedRecordsLength={Object?.keys(selected)?.length || 0}
      useAggregations={view?.ShowAggregations}
      updateAggregation={updateAggregation}
      defaultRecordLayout={view?.DefaultRecordLayout}
      setSelectedPrefill={setSelectedPrefill}
      view={view}
      parentViewID={parentViewID}
      viewMode={viewMode}
      pageID={page?.id}
      draggingID={draggingID}
      setDraggingID={setDraggingID}
      reusableViews={reusableViews}
      commentsWidget={commentsWidget}
      defaultPrefill={defaultPrefill}
      variableValues={variableValues}
    />
  );

  const FinalChildComponent =
    view?.Insights === true ? (
      <PageViewInsights
        ChildComponentRendered={ChildComponentRendered}
        page={page}
        view={view}
        parentViewID={parentViewID}
        record={record}
        query={simpleSearchQuery || query}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        showInsights={showInsights}
      />
    ) : (
      ChildComponentRendered
    );

  const CRDProps: {
    onCreateForm: () => void;
    defaultRecordLayout: string | undefined;
  } = {
    onCreateForm: onCreateForm,
    defaultRecordLayout: selectedPrefill?.Layout || view?.DefaultRecordLayout,
  };

  return (
    <>
      {isMobile ? (
        <>
          <Col className="p-0">
            {!isCalendar && (
              <Row className="m-2">
                <Col className="p-0">
                  <Search
                    isMobile={true}
                    clearSimpleSearch={clearSimpleSearch}
                    query={query}
                    currentModule={currentModule!}
                    setSimpleSearchQuery={setSimpleSearchQuery}
                    setSimpleSearchValue={setSimpleSearchQuery}
                  />
                </Col>
              </Row>
            )}
            <Row className="d-flex justify-content-between align-items-center m-2">
              <div>{QuickCreateMenu}</div>
              <div>{BrieButton}</div>
              <div>{FiltersButton}</div>
              <div>{SortBy}</div>
              <div>{SharedTableLayoutsBtn}</div>
            </Row>
            <div className="d-flex flex-column justify-content-between mt-4 ml-2 mr-2 w-100">
              {FinalChildComponent}
            </div>
          </Col>
        </>
      ) : (
        <>
          <div className="d-flex mb-3 justify-content-between">
            <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
              {QuickCreateMenu}
              {BrieButton}
              {isCalendar ? (
                <div className="pl-1"></div>
              ) : (
                <Search
                  isMobile={false}
                  clearSimpleSearch={clearSimpleSearch}
                  query={query}
                  currentModule={currentModule!}
                  setSimpleSearchQuery={setSimpleSearchQuery}
                  setSimpleSearchValue={setSimpleSearchQuery}
                />
              )}
              {FiltersButton}
              {SortBy}
              {SharedTableLayoutsBtn}
            </div>
            <div className="d-flex">{DataSourceButton}</div>
          </div>
          <TableSettings
            open={tableSettings}
            onClose={toggleTableSettings}
            existingSettings={view}
            saving={viewUpdating}
            onSave={updateTableSettings}
          />
          {FinalChildComponent}
        </>
      )}
      {isSelection && !draggingID && (
        <SharedSelectedRecordsActions
          selectedRecords={selectedRecords}
          onClear={() => setSelected({})}
          setExportDrawer={setExportDrawer}
          setAddToListDrawer={setAddToListDrawer}
          toggleUpdateRecordsModal={toggleUpdateRecordsModal}
          toggleBulkApplyTags={toggleBulkApplyTags}
          toggleMergeDrawer={toggleMergeDrawer}
          deleteSelectedRecords={deleteSelectedRecords}
          toggleConvertDrawer={toggleDataTransforms}
          handleDuplicateRecord={handleDuplicateRecord}
          toggleFeedbackTransformDrawer={() =>
            setBulkFeedbackTransformDrawer(true)
          }
          disabledSidebarCheck={!!parentViewID}
          module={currentModule}
        />
      )}
      {currentModule?.APIName === "Feedback" && (
        <BulkTransformFeedbackDrawer
          open={bulkFeedbackTransformDrawer}
          records={selectedRecords}
          onClose={() => setBulkFeedbackTransformDrawer(false)}
        />
      )}
      <UpdateRecordsDrawer
        open={updateRecordsModal}
        onClose={toggleUpdateRecordsModal}
        records={selectedRecords}
        moduleAPIName={view?.ModuleAPIName}
        view={view}
      />
      <BulkApplyTagsDrawer
        open={bulkApplyTags}
        onClose={toggleBulkApplyTags}
        records={selectedRecords}
        moduleAPIName={view?.ModuleAPIName}
        view={view}
      />
      <DownloadTemplateModal
        open={templateCSVModal}
        handleClose={toggleTemplateCSVModal}
        moduleAPIName={view?.ModuleAPIName}
      />
      <ImportCSVDrawer
        open={importCSVDrawer}
        onClose={toggleImportCSVDrawer}
        onCreate={toggleImportCSVDrawer}
        // @ts-ignore
        moduleAPIName={view?.ModuleAPIName}
        startingFile={null}
        startingHeaders={null}
      />
      <CreateRecordDrawer
        open={createRecordsDrawer}
        onClose={onCreateRecordDrawerClose}
        moduleAPIName={view?.ModuleAPIName}
        onCreate={onCreateRecordDrawerCreate}
        originalRecord={record}
        prefill={
          selectedPrefill
            ? selectedPrefill?.PrefillPairs ?? emptyPrefill
            : emptyPrefill
        }
        {...CRDProps}
      />
      <FormBuilderDrawer
        open={createFormDrawer}
        onClose={() => handleCloseFormBuilder()}
        moduleAPIName={currentModule?.APIName}
        prefill={selectedPrefill || undefined}
        existingForm={null}
        setSuccessFormID={setSuccessFormID}
      />
      <FormBuilderSuccessModal
        open={!!successFormID}
        onClose={() => setSuccessFormID("")}
        formID={successFormID}
      />
      {/* @ts-ignore */}
      {exportDrawer && (
        <DataExportDrawer
          open={exportDrawer}
          moduleAPIName={view?.ModuleAPIName as string}
          onClose={() => setExportDrawer(false)}
          query={query}
          recordIDs={selectedRecordsIds}
        />
      )}
      {addToListDrawer && (
        <AddToListDrawer
          open={addToListDrawer}
          moduleAPIName={view?.ModuleAPIName}
          recordIDs={selectedRecordsIds}
          handleClose={() => setAddToListDrawer(false)}
        />
      )}
      <PageViewSubitemDrawer />
      <MergeRecordsDrawer
        open={mergeDrawer}
        onClose={() => {
          setMergeDrawer(false);
          setSelected({});
        }}
        onMerge={() => setSelected({})}
        records={selectedRecords}
        module={currentModule}
      />
      <SharedViewRecordDrawer
        openRecordData={openRecordData}
        setOpenRecordData={setOpenRecordData}
        view={view}
        toggleTransformIssueDrawer={(currentRecord: any) => {
          setFeedbackLoading(true);
          setFeedbackDrawer(true);
          TransformFeedbackToIssue(currentRecord)
            .then((result) => {
              setFeedbackRecord(result);
              setFeedbackLoading(false);
            })
            .catch((err) => {
              console.error(err);
            });
        }}
      />
      <DuplicateRecordDrawer
        crdProps={CRDProps}
        moduleAPIName={view?.ModuleAPIName}
        recordID={duplicateRecordID}
        open={!!duplicateRecordID}
        onClose={() => setDuplicateRecordID("")}
        resetSelected={() => setSelected({})}
        module={currentModule}
      />
      {currentModule?.APIName === "Feedback" && (
        <FeedbackTransformDrawer
          open={feedbackDrawer}
          onSave={(data: any) =>
            createIssues({
              payload: [data],
              onSuccess: () => {
                setOpenRecordData(null);
                setFeedbackDrawer(false);
              },
            })
          }
          loading={feedbackLoading}
          currentData={feedbackRecord}
          onClose={() => setFeedbackDrawer(false)}
        />
      )}
      <BulkDataTransforms
        open={dTActive}
        records={selectedRecords}
        currentModule={currentModule || undefined}
        onClose={() => {
          setSelected({});
          toggleDataTransforms();
        }}
      />
    </>
  );

  // Handles getting the current selected layout based on the viewMode, _SelectedLayout, and layoutHistory.
  function getSelectedLayout() {
    if (sLayout) return sLayout;

    const isSameMode =
      isMobile && view?.MobileType
        ? view?.MobileType === viewMode
        : view?.Type === viewMode;

    if (isSameMode && view?.Query?._SelectedLayout) {
      // Ensure selected layout still exists
      const exists = !!findWhere(
        view?.[
          layoutTypeMap[viewMode as SharedViewModes]?.itemKey as LayoutKey
        ] ?? [],
        { id: view?.Query?._SelectedLayout }
      );

      if (exists) return view?.Query?._SelectedLayout;
    }

    const existing = layoutHistory[view?.id ?? ""]?.[viewMode as string];

    if (existing) return existing;

    return view?.[
      layoutTypeMap[viewMode as SharedViewModes]?.itemKey as LayoutKey
    ]?.[0]?.id;
  }

  // Grabs the layout based on the viewMode, selectedLayout, _SelectedLayout, and layoutHistory.
  function manageFoundLayout() {
    if (!view || !viewMode) return;

    if (selectedLayout) {
      const layoutfound = findWhere(layoutsToSearch, {
        id: selectedLayout,
      });

      if (layoutfound) return layoutfound;
      setSelectedLayout(null);
    }

    const viewType =
      isMobile && view?.MobileType ? view.MobileType : view?.Type;

    if (viewType === viewMode) {
      return findWhere(layoutsToSearch, {
        id: view?.Query?._SelectedLayout,
      });
    }

    // Handle non default modes - Check Atom for exisitng selected layout
    // If there is no existing selected layout in the Atom, use first layout.
    const previousSelectedLayoutID =
      layoutHistory[view.id]?.[viewMode as string];

    if (previousSelectedLayoutID) {
      return findWhere(layoutsToSearch, {
        id: previousSelectedLayoutID,
      });
    }

    const firstLayoutID = layoutsToSearch?.[0]?.id;

    if (!firstLayoutID) return;

    setLayoutHistory((prev) => ({
      ...prev,
      [view.id]: {
        ...(prev[view.id] ?? {}),
        [viewMode as SharedViewModes]: firstLayoutID,
      },
    }));

    return findWhere(layoutsToSearch, {
      id: firstLayoutID,
    });
  }

  async function deleteSelectedRecords() {
    const confirmed = await confirmBulkDelete();

    if (!confirmed) return;

    // @ts-ignore
    bulkDeleteRecords({
      moduleAPIName: view?.ModuleAPIName as string,
      payload: {
        _UserActivity: {
          WorkspaceID: workspaceID,
          Activity: {
            id: StrUtils.uuidv4(),
            Type: "bulk-delete-in-page",
            Page: {
              id: page.id,
              Name: page.Name,
              Logo: page.Logo,
            },
            View: {
              id: view?.Name,
              Name: view?.Name,
              Type: view?.Type,
            },
          },
        },
        Ids: selectedRecords.map((r: any) => r?.id || r?._id),
      },
      onSuccess: () => {
        setSelected({});
      },
    });
  }

  function handleDuplicateRecord() {
    const r = selectedRecords[0];

    setDuplicateRecordID(r?._id || r?.id);
  }
};

export default SharedToolbar;

export function Search({
  isMobile,
  clearSimpleSearch,
  query,
  currentModule,
  setSimpleSearchQuery,
  setSimpleSearchValue,
}: {
  isMobile: boolean;
  clearSimpleSearch: () => void;
  query: any;
  currentModule: Module;
  setSimpleSearchQuery: (str: string) => void;
  setSimpleSearchValue: (str: string) => void;
}) {
  return (
    <SharedSearch
      clearSimpleSearch={clearSimpleSearch}
      query={query}
      currentModule={currentModule}
      setSimpleSearchQuery={setSimpleSearchQuery}
      setSimpleSearchValue={setSimpleSearchValue}
      isMobile={!!isMobile}
    />
  );
}
